import React, { useEffect, useRef, useState } from 'react';
import PressableDynamicOpacity from '../../../v2BaseComponents/PressableDynamicOpacity';
import { Platform, View } from 'react-native';
import Stack from '../../../v2BaseComponents/Stack';
import Text from '../../../v2BaseComponents/Text';
import { Animated } from 'react-native';
import { COLLAPSE_ANIMATION_DURATION } from './constants';
import Inset from './Inset';
import { colors } from '../../../../utils/theme';
import HoverDetectionContainer from '../../../v2BaseComponents/HoverDetectionContainer';
import { hexToRgbA } from '../../../../utils/hexToRGBA';
import { useAppType } from '../../../../hooks/useAppType';

export type NavigationItemOwnProps = {
    icon: any;
    isLargeIcon?: boolean;
    isActive: boolean;
    isCollapsed: boolean;
    onPress: () => void;
    testID?: string;
    display: string;
    isDisabled?: boolean;
    smallFont?: boolean;
    rightContent?: React.ReactNode;
};

const EXPANDED_PADDING = 12;
const COLLAPSED_PADDING = 10;

export type NavigationItemProps = NavigationItemOwnProps;

const NavigationItem: React.FC<NavigationItemProps> = (props) => {
    const isProApp = useAppType() === 'professional';
    const [isHovered, setIsHovered] = useState(false);
    const textOpacityAnimation = useRef<Animated.Value>(new Animated.Value(props.isCollapsed ? 0 : 1));
    const paddingAnimation = useRef<Animated.Value>(
        new Animated.Value(props.isCollapsed ? COLLAPSED_PADDING : EXPANDED_PADDING),
    );
    const expandedPadding = props.isLargeIcon ? EXPANDED_PADDING - 1.5 : EXPANDED_PADDING;
    const collapsedPadding = props.isLargeIcon ? COLLAPSED_PADDING - 1.5 : COLLAPSED_PADDING;
    useEffect(() => {
        if (!textOpacityAnimation.current || !paddingAnimation.current) return;

        Animated.timing(textOpacityAnimation.current, {
            toValue: props.isCollapsed ? 0 : 1,
            duration: COLLAPSE_ANIMATION_DURATION,
            useNativeDriver: Platform.OS === 'web',
        }).start();

        Animated.timing(paddingAnimation.current, {
            toValue: props.isCollapsed ? collapsedPadding : expandedPadding,
            duration: COLLAPSE_ANIMATION_DURATION,
            useNativeDriver: Platform.OS === 'web',
        }).start();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isCollapsed]);

    return (
        <View
            style={{
                width: '100%',
                height: 38,
                display: 'flex',
                flexDirection: 'row',
            }}
        >
            <Inset isCollapsed={props.isCollapsed} />
            <HoverDetectionContainer onHoverChange={setIsHovered} style={{ flex: 1 }}>
                <PressableDynamicOpacity
                    isDisabled={props.isDisabled}
                    onPress={props.onPress}
                    testID={props.testID}
                    style={{
                        borderRadius: 5,
                        overflow: 'hidden',
                        flex: 1,
                        height: '100%',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        flexDirection: 'row',
                        backgroundColor: isHovered
                            ? hexToRgbA(colors['Primary-Light'], 0.1)
                            : props.isActive
                            ? isProApp
                                ? hexToRgbA(colors['Pro-Drawer-Item-Active'], 0.1)
                                : colors['Drawer-Item-Active']
                            : undefined,
                        paddingTop: 0,
                        paddingBottom: 0,
                        width: '100%',
                    }}
                >
                    <Animated.View
                        style={{
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            flexDirection: 'row',
                            paddingLeft: paddingAnimation.current,
                            paddingRight: paddingAnimation.current,
                            flex: 1,
                        }}
                        collapsable={false}
                    >
                        <Stack
                            style={{
                                justifyContent: 'center',
                                marginRight: 8,
                            }}
                            horizontal
                        >
                            {props.icon}
                        </Stack>
                        <Animated.View
                            style={[
                                textOpacityAnimation.current !== null ? { opacity: textOpacityAnimation.current } : {},
                                {
                                    flex: 1,
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flexDirection: 'row',
                                },
                            ]}
                        >
                            <Text variant="h4" color="White" style={{ letterSpacing: -0.5, flex: 1 }}>
                                {props.display}
                            </Text>
                            {props.rightContent ? props.rightContent : null}
                        </Animated.View>
                    </Animated.View>
                </PressableDynamicOpacity>
            </HoverDetectionContainer>
            <Inset isCollapsed={props.isCollapsed} />
        </View>
    );
};

export default React.memo(NavigationItem);
